import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  EnvelopeSimple,
  Phone,
  MapPin,
  Calendar,
  ShoppingBag,
  CurrencyDollar,
  Tag,
  FileText,
  PencilSimple,
  FloppyDisk,
  X,
  CheckCircle,
  XCircle,
  WarningCircle,
  Package,
  Clock,
} from '@phosphor-icons/react';
import { apiClient, useAuthStore } from '@dukene/shared';
import { format } from 'date-fns';

enum CustomerStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  BLOCKED = 'BLOCKED',
}

enum OrderStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  CONFIRMED = 'CONFIRMED',
  SHIPPED = 'SHIPPED',
  DELIVERED = 'DELIVERED',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED',
}

enum PaymentStatus {
  PENDING = 'PENDING',
  PAID = 'PAID',
  FAILED = 'FAILED',
  REFUNDED = 'REFUNDED',
}

interface Address {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  address: string;
  city: string;
  state: string | null;
  country: string;
  postalCode: string | null;
  isDefault: boolean;
}

interface Order {
  id: string;
  orderNumber: string;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  total: number;
  createdAt: string;
  itemCount?: number;
}

interface Customer {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  status: CustomerStatus;
  acceptsMarketing: boolean;
  totalSpent: number;
  notes: string | null;
  tags: string[] | null;
  createdAt: string;
  addresses: Address[];
  orders: Order[];
  _count: {
    orders: number;
  };
}

const statusColors: Record<CustomerStatus, string> = {
  [CustomerStatus.ACTIVE]: 'bg-[#E3F1DF] text-[#006838] dark:bg-emerald-950/40 dark:text-emerald-400',
  [CustomerStatus.INACTIVE]: 'bg-[#F6F6F7] text-[#5C5F62] dark:bg-slate-800 dark:text-slate-300',
  [CustomerStatus.BLOCKED]: 'bg-[#FFEA8A] text-[#8A6116] dark:bg-amber-950/40 dark:text-amber-400',
};

const orderStatusColors: Record<string, string> = {
  [OrderStatus.PENDING]: 'bg-[#FFF5EA] text-[#B98900] dark:bg-amber-950/40 dark:text-amber-400',
  [OrderStatus.PROCESSING]: 'bg-[#EBF5FF] text-[#2C6ECB] dark:bg-blue-950/40 dark:text-blue-400',
  [OrderStatus.CONFIRMED]: 'bg-[#EBF5FF] text-[#2C6ECB] dark:bg-cyan-950/40 dark:text-cyan-400',
  [OrderStatus.SHIPPED]: 'bg-[#F4EBFB] text-[#7E43A8] dark:bg-purple-950/40 dark:text-purple-400',
  [OrderStatus.DELIVERED]: 'bg-[#E3F1DF] text-[#006838] dark:bg-emerald-950/40 dark:text-emerald-400',
  [OrderStatus.CANCELLED]: 'bg-[#FFEA8A]/50 text-[#DE3618] dark:bg-red-950/40 dark:text-red-400',
  [OrderStatus.REFUNDED]: 'bg-[#FFF5EA] text-[#B98900] dark:bg-amber-950/40 dark:text-amber-400',
};

const paymentStatusColors: Record<string, string> = {
  [PaymentStatus.PENDING]: 'bg-[#FFF5EA] text-[#B98900] dark:bg-amber-950/40 dark:text-amber-400',
  [PaymentStatus.PAID]: 'bg-[#E3F1DF] text-[#006838] dark:bg-emerald-950/40 dark:text-emerald-400',
  [PaymentStatus.FAILED]: 'bg-[#FFEA8A]/50 text-[#DE3618] dark:bg-red-950/40 dark:text-red-400',
  [PaymentStatus.REFUNDED]: 'bg-[#FFF5EA] text-[#B98900] dark:bg-amber-950/40 dark:text-amber-400',
};

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const activeStoreId = useAuthStore((state) => state.activeStoreId);
  const [isEditing, setIsEditing] = useState(false);
  const [editedCustomer, setEditedCustomer] = useState<Partial<Customer>>({});

  // Fetch customer details
  const {
    data: customer,
    isLoading,
    error,
  } = useQuery<Customer>({
    queryKey: ['customer', activeStoreId, id],
    queryFn: async () => {
      const response = await apiClient.get<Customer>(`/customers/merchant/${id}`);
      return response;
    },
    enabled: !!id && !!activeStoreId,
  });

  // Update customer mutation
  const updateMutation = useMutation({
    mutationFn: async (data: Partial<Customer>) => {
      return apiClient.patch(`/customers/merchant/${id}`, data);
    },
    onSuccess: () => {
      const storeId = useAuthStore.getState().activeStoreId;
      queryClient.invalidateQueries({ queryKey: ['customer', storeId, id] });
      queryClient.invalidateQueries({ queryKey: ['merchant-customers', storeId] });
      setIsEditing(false);
      setEditedCustomer({});
    },
  });

  const handleEdit = () => {
    setEditedCustomer({
      firstName: customer?.firstName,
      lastName: customer?.lastName,
      phone: customer?.phone,
      status: customer?.status,
      notes: customer?.notes,
      tags: customer?.tags,
      acceptsMarketing: customer?.acceptsMarketing,
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedCustomer({});
  };

  const handleSave = () => {
    updateMutation.mutate(editedCustomer);
  };

  const updateField = <K extends keyof Customer>(field: K, value: Customer[K]) => {
    setEditedCustomer((prev) => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-3 border-[#2C6ECB] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="max-w-2xl mx-auto mt-12 bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-8 text-center shadow-sm">
        <div className="w-12 h-12 rounded-full bg-red-50 dark:bg-red-950/40 text-red-600 flex items-center justify-center mx-auto mb-4">
          <WarningCircle weight="bold" className="h-6 w-6" />
        </div>
        <h3 className="text-base font-bold text-[#202223] dark:text-white mb-1">Error loading customer</h3>
        <p className="text-sm text-[#5C5F62] dark:text-slate-400 mb-6">
          {error instanceof Error ? error.message : 'The requested customer profile could not be found.'}
        </p>
        <button
          onClick={() => navigate('/customers')}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#F6F6F7] hover:bg-gray-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-[#202223] dark:text-white text-xs font-semibold rounded-lg transition-colors"
        >
          <ArrowLeft weight="bold" className="h-4 w-4" />
          Back to Customers
        </button>
      </div>
    );
  }

  const displayCustomer = isEditing ? { ...customer, ...editedCustomer } : customer;
  const statusColor = statusColors[displayCustomer.status] || 'bg-[#F6F6F7] text-[#5C5F62] dark:bg-slate-800 dark:text-slate-300';
  const totalOrders = customer._count?.orders || customer.orders?.length || 0;
  const averageOrderValue = totalOrders > 0 ? (customer.totalSpent || 0) / totalOrders : 0;
  const defaultAddress = customer.addresses?.find((addr) => addr.isDefault);

  return (
    <div className="space-y-6 pb-12 max-w-7xl mx-auto">
      {/* High-Density Polaris Admin Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4.5 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/customers')}
            className="p-2 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 rounded-lg text-[#5C5F62] dark:text-slate-400 transition-colors"
            title="Back to Customers"
          >
            <ArrowLeft weight="bold" className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-lg font-bold text-[#202223] dark:text-white">
                {displayCustomer.firstName} {displayCustomer.lastName}
              </h1>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${statusColor}`}>
                {displayCustomer.status}
              </span>
            </div>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5 font-mono">
              {displayCustomer.email}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-xs font-semibold text-[#5C5F62] hover:bg-[#F6F6F7] dark:text-slate-300 dark:hover:bg-slate-800 rounded-lg flex items-center gap-1.5 transition-colors border border-[#D2D5D9] dark:border-slate-700"
                disabled={updateMutation.isPending}
              >
                <X weight="bold" className="h-3.5 w-3.5" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-xs font-bold bg-[#2C6ECB] hover:bg-[#1f57a4] text-white rounded-lg flex items-center gap-1.5 shadow-sm transition-colors disabled:opacity-50"
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FloppyDisk weight="bold" className="h-3.5 w-3.5" />
                    Save Changes
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={handleEdit}
              className="flex items-center justify-center gap-1.5 bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#202223] dark:text-white px-3.5 py-2 rounded-lg text-xs font-bold transition-colors shadow-sm"
            >
              <PencilSimple weight="bold" className="h-3.5 w-3.5" />
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {/* KPI Metric Summary Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 p-4 rounded-xl shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-semibold text-[#5C5F62] dark:text-slate-400 uppercase tracking-wider">
              Total Orders
            </span>
            <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-1">
              {totalOrders}
            </p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-[#F6F6F7] dark:bg-slate-800 flex items-center justify-center text-[#2C6ECB]">
            <ShoppingBag weight="fill" className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 p-4 rounded-xl shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-semibold text-[#5C5F62] dark:text-slate-400 uppercase tracking-wider">
              Total Spent
            </span>
            <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-1">
              KES {(customer.totalSpent || 0).toLocaleString()}
            </p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-[#E3F1DF] dark:bg-emerald-950/40 text-[#006838] dark:text-emerald-400 flex items-center justify-center">
            <CurrencyDollar weight="fill" className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 p-4 rounded-xl shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-semibold text-[#5C5F62] dark:text-slate-400 uppercase tracking-wider">
              Average Order
            </span>
            <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-1">
              KES {averageOrderValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-[#F4EBFB] dark:bg-purple-950/40 text-[#7E43A8] dark:text-purple-400 flex items-center justify-center">
            <Package weight="fill" className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 p-4 rounded-xl shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-semibold text-[#5C5F62] dark:text-slate-400 uppercase tracking-wider">
              Customer Since
            </span>
            <p className="text-base font-bold text-[#202223] dark:text-white mt-1 pt-0.5">
              {customer.createdAt ? format(new Date(customer.createdAt), 'MMM yyyy') : 'N/A'}
            </p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-[#FFF5EA] dark:bg-amber-950/40 text-[#B98900] dark:text-amber-400 flex items-center justify-center">
            <Calendar weight="fill" className="w-5 h-5" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Information Card */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 shadow-sm">
            <h2 className="text-base font-bold text-[#202223] dark:text-white mb-4 flex items-center gap-2">
              <EnvelopeSimple weight="fill" className="h-4 w-4 text-[#5C5F62] dark:text-slate-400" />
              Customer Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#5C5F62] dark:text-slate-400 mb-1">
                  First Name
                </label>
                {isEditing ? (
                  <input
                    value={editedCustomer.firstName || ''}
                    onChange={(e) => updateField('firstName', e.target.value)}
                    className="w-full h-8 px-3 text-xs border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-[#141618] rounded-lg text-[#202223] dark:text-white focus:outline-none focus:border-[#2C6ECB]"
                  />
                ) : (
                  <p className="text-sm font-medium text-[#202223] dark:text-white">
                    {displayCustomer.firstName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#5C5F62] dark:text-slate-400 mb-1">
                  Last Name
                </label>
                {isEditing ? (
                  <input
                    value={editedCustomer.lastName || ''}
                    onChange={(e) => updateField('lastName', e.target.value)}
                    className="w-full h-8 px-3 text-xs border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-[#141618] rounded-lg text-[#202223] dark:text-white focus:outline-none focus:border-[#2C6ECB]"
                  />
                ) : (
                  <p className="text-sm font-medium text-[#202223] dark:text-white">
                    {displayCustomer.lastName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#5C5F62] dark:text-slate-400 mb-1">
                  Email Address
                </label>
                <p className="text-sm font-medium text-[#202223] dark:text-white font-mono">
                  {displayCustomer.email}
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#5C5F62] dark:text-slate-400 mb-1">
                  Phone Number
                </label>
                {isEditing ? (
                  <input
                    value={editedCustomer.phone || ''}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="e.g. +254 712 345 678"
                    className="w-full h-8 px-3 text-xs border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-[#141618] rounded-lg text-[#202223] dark:text-white focus:outline-none focus:border-[#2C6ECB]"
                  />
                ) : (
                  <p className="text-sm font-medium text-[#202223] dark:text-white font-mono">
                    {displayCustomer.phone || 'Not provided'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#5C5F62] dark:text-slate-400 mb-1">
                  Account Status
                </label>
                {isEditing ? (
                  <select
                    value={editedCustomer.status || CustomerStatus.ACTIVE}
                    onChange={(e) => updateField('status', e.target.value as CustomerStatus)}
                    className="w-full h-8 px-2.5 text-xs border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-[#141618] rounded-lg text-[#202223] dark:text-white focus:outline-none focus:border-[#2C6ECB]"
                  >
                    <option value={CustomerStatus.ACTIVE}>Active</option>
                    <option value={CustomerStatus.INACTIVE}>Inactive</option>
                    <option value={CustomerStatus.BLOCKED}>Blocked</option>
                  </select>
                ) : (
                  <div className="mt-0.5">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold ${statusColor}`}>
                      {displayCustomer.status}
                    </span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#5C5F62] dark:text-slate-400 mb-1">
                  Marketing Preferences
                </label>
                <div className="mt-1 flex items-center gap-2">
                  {isEditing ? (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editedCustomer.acceptsMarketing ?? false}
                        onChange={(e) => updateField('acceptsMarketing', e.target.checked)}
                        className="h-4 w-4 rounded border-[#D2D5D9] text-[#2C6ECB] focus:ring-[#2C6ECB]"
                      />
                      <span className="text-xs font-medium text-[#202223] dark:text-white">
                        Subscribed to promotional campaigns
                      </span>
                    </label>
                  ) : (
                    <>
                      {displayCustomer.acceptsMarketing ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#E3F1DF] text-[#006838] dark:bg-emerald-950/40 dark:text-emerald-400">
                          <CheckCircle weight="fill" className="h-3.5 w-3.5" />
                          Subscribed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#F6F6F7] text-[#5C5F62] dark:bg-slate-800 dark:text-slate-300">
                          <XCircle weight="fill" className="h-3.5 w-3.5" />
                          Unsubscribed
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Default Address Card */}
          {defaultAddress && (
            <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 shadow-sm">
              <h2 className="text-base font-bold text-[#202223] dark:text-white mb-3 flex items-center gap-2">
                <MapPin weight="fill" className="h-4 w-4 text-[#5C5F62] dark:text-slate-400" />
                Default Address
              </h2>
              <div className="bg-[#F6F6F7] dark:bg-slate-800/60 rounded-lg p-3.5 space-y-1 text-xs text-[#303030] dark:text-slate-300">
                <p className="font-bold text-sm text-[#202223] dark:text-white">
                  {defaultAddress.firstName} {defaultAddress.lastName}
                </p>
                {defaultAddress.phone && (
                  <p className="flex items-center gap-1.5 text-[#5C5F62] dark:text-slate-400 py-0.5">
                    <Phone weight="bold" className="h-3.5 w-3.5" />
                    {defaultAddress.phone}
                  </p>
                )}
                <p>{defaultAddress.address}</p>
                <p>
                  {defaultAddress.city}
                  {defaultAddress.state && `, ${defaultAddress.state}`}
                  {defaultAddress.postalCode && ` ${defaultAddress.postalCode}`}
                </p>
                <p className="font-semibold pt-1 text-[#5C5F62] dark:text-slate-400">{defaultAddress.country}</p>
              </div>
            </div>
          )}

          {/* Recent Orders Card */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-[#202223] dark:text-white flex items-center gap-2">
                <ShoppingBag weight="fill" className="h-4 w-4 text-[#5C5F62] dark:text-slate-400" />
                Recent Orders ({customer.orders?.length || 0})
              </h2>
              {totalOrders > 0 && (
                <Link
                  to="/orders"
                  className="text-xs font-semibold text-[#2C6ECB] hover:underline flex items-center gap-1"
                >
                  View all orders
                </Link>
              )}
            </div>

            {customer.orders && customer.orders.length > 0 ? (
              <div className="divide-y divide-[#E1E3E5] dark:divide-slate-800 -mx-5 px-5">
                {customer.orders.map((order) => (
                  <div
                    key={order.id}
                    className="py-3.5 flex items-center justify-between gap-4 hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors -mx-5 px-5"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <Link
                          to={`/orders/${order.id}`}
                          className="font-bold text-xs text-[#2C6ECB] hover:underline"
                        >
                          #{order.orderNumber}
                        </Link>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${orderStatusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                          {order.status}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${paymentStatusColors[order.paymentStatus] || 'bg-gray-100 text-gray-800'}`}>
                          {order.paymentStatus}
                        </span>
                      </div>
                      <p className="text-xs text-[#8C9196] dark:text-slate-500 flex items-center gap-1.5 font-medium">
                        <Clock weight="bold" className="h-3 w-3" />
                        {order.createdAt ? format(new Date(order.createdAt), 'MMM dd, yyyy h:mm a') : 'N/A'}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-bold text-sm text-[#202223] dark:text-white font-mono">
                        KES {order.total.toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10 bg-[#F6F6F7]/50 dark:bg-slate-800/40 rounded-xl border border-dashed border-[#D2D5D9] dark:border-slate-800">
                <ShoppingBag weight="duotone" className="h-10 w-10 mx-auto mb-2 text-[#8C9196] dark:text-slate-500" />
                <p className="text-sm font-semibold text-[#202223] dark:text-white mb-0.5">No order history yet</p>
                <p className="text-xs text-[#5C5F62] dark:text-slate-400">
                  When this customer completes a purchase, their orders will appear here.
                </p>
              </div>
            )}
          </div>

          {/* All Addresses Card */}
          {customer.addresses && customer.addresses.length > 0 && (
            <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 shadow-sm">
              <h2 className="text-base font-bold text-[#202223] dark:text-white mb-4 flex items-center gap-2">
                <MapPin weight="fill" className="h-4 w-4 text-[#5C5F62] dark:text-slate-400" />
                All Addresses ({customer.addresses.length})
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {customer.addresses.map((address) => (
                  <div
                    key={address.id}
                    className="p-3.5 bg-[#F6F6F7] dark:bg-slate-800/60 border border-[#E1E3E5] dark:border-slate-700/60 rounded-xl flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="font-bold text-xs text-[#202223] dark:text-white">
                          {address.firstName} {address.lastName}
                        </p>
                        {address.isDefault && (
                          <span className="px-2 py-0.5 bg-[#EBF5FF] dark:bg-blue-950/40 text-[#2C6ECB] dark:text-blue-400 text-[10px] font-bold rounded">
                            Default
                          </span>
                        )}
                      </div>
                      {address.phone && (
                        <p className="text-xs text-[#5C5F62] dark:text-slate-400 mb-1 flex items-center gap-1">
                          <Phone weight="bold" className="h-3 w-3" />
                          {address.phone}
                        </p>
                      )}
                      <p className="text-xs text-[#303030] dark:text-slate-300">{address.address}</p>
                      <p className="text-xs text-[#5C5F62] dark:text-slate-400">
                        {address.city}, {address.country}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Secondary Info & Timeline */}
        <div className="space-y-6">
          {/* Tags Card */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 shadow-sm">
            <h2 className="text-base font-bold text-[#202223] dark:text-white mb-3 flex items-center gap-2">
              <Tag weight="fill" className="h-4 w-4 text-[#5C5F62] dark:text-slate-400" />
              Customer Tags
            </h2>
            {isEditing ? (
              <div>
                <input
                  value={editedCustomer.tags?.join(', ') || ''}
                  onChange={(e) =>
                    updateField(
                      'tags',
                      e.target.value
                        .split(',')
                        .map((t) => t.trim())
                        .filter(Boolean)
                    )
                  }
                  placeholder="e.g. VIP, Wholesale, Repeat"
                  className="w-full h-8 px-3 text-xs border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-[#141618] rounded-lg text-[#202223] dark:text-white focus:outline-none focus:border-[#2C6ECB]"
                />
                <p className="text-[11px] text-[#8C9196] dark:text-slate-500 mt-1">
                  Separate multiple tags with commas.
                </p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {displayCustomer.tags && displayCustomer.tags.length > 0 ? (
                  displayCustomer.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#F6F6F7] text-[#303030] dark:bg-slate-800 dark:text-slate-300 border border-[#D2D5D9] dark:border-slate-700 shadow-2xs"
                    >
                      {tag}
                    </span>
                  ))
                ) : (
                  <p className="text-xs text-[#8C9196] dark:text-slate-500 italic">No tags assigned to this customer</p>
                )}
              </div>
            )}
          </div>

          {/* Notes Card */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 shadow-sm">
            <h2 className="text-base font-bold text-[#202223] dark:text-white mb-3 flex items-center gap-2">
              <FileText weight="fill" className="h-4 w-4 text-[#5C5F62] dark:text-slate-400" />
              Notes
            </h2>
            {isEditing ? (
              <textarea
                value={editedCustomer.notes || ''}
                onChange={(e) => updateField('notes', e.target.value)}
                placeholder="Add private notes about this customer..."
                className="w-full min-h-[100px] p-3 text-xs border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-[#141618] rounded-lg text-[#202223] dark:text-white focus:outline-none focus:border-[#2C6ECB] resize-none"
              />
            ) : (
              <div className="p-3.5 bg-[#F6F6F7]/60 dark:bg-slate-800/40 rounded-xl border border-[#E1E3E5] dark:border-slate-700/50">
                <p className="text-xs text-[#303030] dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
                  {displayCustomer.notes || 'No private notes recorded for this customer.'}
                </p>
              </div>
            )}
          </div>

          {/* Customer Timeline Card */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 shadow-sm">
            <h2 className="text-base font-bold text-[#202223] dark:text-white mb-4 flex items-center gap-2">
              <Calendar weight="fill" className="h-4 w-4 text-[#5C5F62] dark:text-slate-400" />
              Activity Timeline
            </h2>
            <div className="space-y-4 relative before:absolute before:left-[5px] before:top-2 before:bottom-2 before:w-[2px] before:bg-[#E1E3E5] dark:before:bg-slate-800">
              <div className="flex items-start gap-3 relative">
                <div className="w-3 h-3 mt-1 rounded-full bg-[#2C6ECB] ring-4 ring-white dark:ring-[#1A1D21] shrink-0 z-10" />
                <div>
                  <p className="font-semibold text-xs text-[#202223] dark:text-white">Customer Profile Created</p>
                  <p className="text-[11px] text-[#8C9196] dark:text-slate-500 font-medium">
                    {customer.createdAt ? format(new Date(customer.createdAt), 'MMM dd, yyyy h:mm a') : 'N/A'}
                  </p>
                </div>
              </div>

              {customer.orders && customer.orders.length > 0 && (
                <>
                  <div className="flex items-start gap-3 relative">
                    <div className="w-3 h-3 mt-1 rounded-full bg-emerald ring-4 ring-white dark:ring-[#1A1D21] shrink-0 z-10" />
                    <div>
                      <p className="font-semibold text-xs text-[#202223] dark:text-white">First Order Placed</p>
                      <p className="text-[11px] text-[#8C9196] dark:text-slate-500 font-medium">
                        {customer.orders[customer.orders.length - 1]?.createdAt
                          ? format(new Date(customer.orders[customer.orders.length - 1].createdAt), 'MMM dd, yyyy')
                          : 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 relative">
                    <div className="w-3 h-3 mt-1 rounded-full bg-purple-500 ring-4 ring-white dark:ring-[#1A1D21] shrink-0 z-10" />
                    <div>
                      <p className="font-semibold text-xs text-[#202223] dark:text-white">Most Recent Purchase</p>
                      <p className="text-[11px] text-[#8C9196] dark:text-slate-500 font-medium">
                        {customer.orders[0]?.createdAt
                          ? format(new Date(customer.orders[0].createdAt), 'MMM dd, yyyy')
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
